
import moment from "moment";
import AWS from "aws-sdk";
import axios from "axios";
import { API } from "../../../config/API";
import { getUploadConfig } from "../../../config/uploadConfig";

// Type definitions for PDF processing results
type PageResult = 
  | { pageNum: number; imageFile: File }
  | { pageNum: number; error: any };


const configureS3 = () => {
  
  AWS.config.update({
 
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    region: process.env.REACT_APP_AWS_REGION

  });


  return new AWS.S3({
    params: { Bucket: "bairuha-bucket" }
  });
};

const convertApi = async (
  file: any,
  type: any,
  index: any,
  quality: any,
  editon: any,
  date: any
) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Initialize S3
      const s3 = configureS3();

      // Format date consistently
      const formattedDate = moment(date).format("DD-MM-YYYY");

      // Create a unique filename with timestamp
      const timestamp = moment().format("YYYYMMDDHHmmss");
      const fileExtension = file.name.split('.').pop();
      const key = `epaper/${editon}/${formattedDate}/${type}/page_${index}_${timestamp}.${fileExtension}`;

      const params = {
        Bucket: "bairuha-bucket",
        Key: key,
        Body: file,
        ContentType: file.type,
        ACL: 'public-read',
        Metadata: {
          'type': type.toString(),
          'index': index.toString(),
          'quality': quality.toString(),
          'editon': editon.toString(),
          'date': formattedDate
        }
      };
      // Upload to S3
      s3.upload(params, (err: Error, data: AWS.S3.ManagedUpload.SendData) => {
        if (err) {
          console.log("S3 upload error = = = >", err);
          reject(err);
          return;
        }

        // Return response in the same format as your API would
        resolve({
          success: true,
          image_url: data.Location,
          pdf_url: data.Location,
          key: data.Key,
          type: type,
          index: index,
          quality: quality,
          editon: editon,
          date: formattedDate
        });
      });
    } catch (error) {
      console.log("convertApi error = = = >", error);
      reject(error);
    }
  });
};

const UploadApi = async (
  file: any,
  type: any,
  index: any,
  quality: any,
  editon: any,
  date: any
) => {
  console.log("=====file=======", file);
  return new Promise(async (resolve, reject) => {
    try {
      // Initialize S3
      const s3 = configureS3();

      // Format date consistently
      const formattedDate = moment(date).format("DD-MM-YYYY");

      // Create a unique filename with timestamp
      const timestamp = moment().format("YYYYMMDDHHmmss");
      const fileExtension = file.name.split('.').pop();
      const key = `epaper/${editon}/${formattedDate}/${type}/quality_${quality}/image_${index}_${timestamp}.${fileExtension}`;

      // Set up upload parameters
      const params = {
        Bucket: "bairuha-bucket",
        Key: key,
        Body: file,
        ContentType: file.type,
        ACL: 'public-read',
        Metadata: {
          'type': type.toString(),
          'index': index.toString(),
          'quality': quality.toString(),
          'editon': editon.toString(),
          'date': formattedDate
        }
      };
      // Upload to S3 with callback pattern instead of promises
      s3.upload(params, (err: Error, data: any) => {
        if (err) {
          console.log("S3 upload error = = = >", err);
          reject(err);
          return;
        }

        const response = {
          success: true,
          image_url: data.Location,
          key: data.Key,
          type: type,
          index: index,
          quality: quality,
          editon: editon,
          date: formattedDate
        };

        console.log("=====response========", response);
        resolve(response);
      });
    } catch (error) {
      console.log("convertApi error = = = >", error);
      reject(error);
    }
  });
};

// Optimized PDF processing functions
export const optimizePdfToImages = async (file: File, maxConcurrency?: number, onProgress?: (progress: number, status: string) => void): Promise<string[]> => {
  const concurrency = maxConcurrency || getUploadConfig('PDF.MAX_CONCURRENCY') || 3;
  const renderScale = getUploadConfig('PDF.RENDER_SCALE') || 1.2;
  const jpegQuality = getUploadConfig('PDF.JPEG_QUALITY') || 0.8;
  return new Promise<string[]>(async (resolve, reject) => {
    try {
      const uri = URL.createObjectURL(file);
      const PDFJS = window.pdfjsLib;
      
      onProgress?.(10, "Loading PDF document...");
      const pdf = await PDFJS.getDocument({ url: uri }).promise;
      const totalPages = pdf.numPages;
      const imageUrls: string[] = [];
      
      onProgress?.(20, `Processing ${totalPages} pages...`);
      
      // Process pages in batches to avoid overwhelming the browser
      const processBatch = async (startPage: number, endPage: number) => {
        const batchPromises = [];
        
                for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
          const pagePromise = (async () => {
            try {
              const page = await pdf.getPage(pageNum);
              // Use configurable scale for faster processing and smaller file size
              const viewport = page.getViewport({ scale: renderScale });
              
              const canvas = document.createElement("canvas");
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              
              const renderContext = {
                canvasContext: canvas.getContext("2d"),
                viewport: viewport,
              };
              
              if (!renderContext.canvasContext) {
                throw new Error(`Failed to get 2D canvas context for page ${pageNum}`);
              }
              
              await page.render(renderContext).promise;
              
              // Use JPEG with configurable compression for smaller file size
              const imageUrl = canvas.toDataURL("image/jpeg", jpegQuality);
              if (!imageUrl || imageUrl === "data:,") {
                throw new Error(`Canvas failed to generate a valid image URL for page ${pageNum}`);
              }
              
              // Convert data URL to Blob for S3 upload
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              const imageFile = new File([blob], `page_${pageNum}.jpg`, {
                type: "image/jpeg",
              });
              
              return { pageNum, imageFile } as { pageNum: number; imageFile: File };
            } catch (error) {
              console.error(`Error processing page ${pageNum}:`, error);
              return { pageNum, error } as { pageNum: number; error: any };
            }
          })();
          
          batchPromises.push(pagePromise);
        }
        
        return Promise.all(batchPromises);
      };
      
      // Process all pages in batches
      const allResults: PageResult[] = [];
      for (let i = 1; i <= totalPages; i += concurrency) {
        const endPage = Math.min(i + concurrency - 1, totalPages);
        const progress = 20 + Math.floor((i / totalPages) * 40);
        onProgress?.(progress, `Converting pages ${i}-${endPage} of ${totalPages}...`);
        const batchResults = await processBatch(i, endPage);
        allResults.push(...batchResults);
      }
      
      // Sort results by page number and upload to S3
      allResults.sort((a, b) => a.pageNum - b.pageNum);
      
      onProgress?.(60, "Uploading images to server...");
      
      // Upload all images to S3 in parallel
      const uploadPromises = allResults
        .filter((result): result is { pageNum: number; imageFile: File } => 
          !('error' in result) && 'imageFile' in result
        )
        .map(async (result) => {
          try {
            const uploadResponse = await uploadImageToS3Optimized(result.imageFile);
            if (!uploadResponse?.data?.data) {
              throw new Error(`No upload response data for page ${result.pageNum}`);
            }
            return {
              pageNum: result.pageNum,
              s3Url: uploadResponse.data.data
            };
          } catch (error) {
            console.error(`Error uploading page ${result.pageNum}:`, error);
            return { pageNum: result.pageNum, error };
          }
        });
      
      const uploadResults = await Promise.all(uploadPromises);
      onProgress?.(90, "Finalizing upload...");
      
      // Sort by page number and extract URLs
      uploadResults
        .filter(result => !result.error)
        .sort((a, b) => a.pageNum - b.pageNum)
        .forEach(result => {
          imageUrls.push(result.s3Url);
        });
      
      URL.revokeObjectURL(uri);
      resolve(imageUrls as string[]);
    } catch (error) {
      console.error("PDF optimization error:", error);
      reject(error);
    }
  });
};

// Optimized S3 upload with better error handling and retry logic
export const uploadImageToS3Optimized = async (file: File, retries?: number): Promise<any> => {
  const maxRetries = retries || getUploadConfig('S3.MAX_RETRIES') || 3;
  const timeout = getUploadConfig('S3.TIMEOUT') || 30000;
  const retryDelay = getUploadConfig('S3.RETRY_DELAY') || 1000;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const formData = new FormData();
      const filename = `${moment().format("YYYY-MM-DDHH:MM:SS")}${file?.name}`;
      formData.append("files", file, filename);
      
      const response = await axios.post(`${API.BASE_URL + API.UPLOAD_IMAGE_OR_PDF}`, formData, {
        timeout: timeout,
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      return response;
    } catch (error) {
      console.error(`Upload attempt ${attempt} failed:`, error);
      if (attempt === maxRetries) {
        throw error;
      }
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
    }
  }
};

export { convertApi, UploadApi };